'use client';

import * as React from 'react';
import {
  Award,
  BarChart,
  BarChart3,
  Bell,
  BookOpen,
  Brain,
  Briefcase,
  Building,
  Building2,
  Code,
  Dumbbell,
  FileCode,
  FileText,
  Globe,
  GraduationCap,
  HeadphonesIcon,
  HelpCircle,
  Laptop,
  Layout,
  Lightbulb,
  LucideIcon,
  Megaphone,
  MonitorPlay,
  Presentation,
  Rocket,
  School,
  Shield,
  Smartphone,
  Sparkles,
  Target,
  UserCheck,
  Users,
  Youtube
} from 'lucide-react';

import { WhoIsItForProps } from '~/types/who-is-it-for-type';
import { BlurFade } from '../fragments/blur-fade';
import { SiteHeading } from '../fragments/site-heading';

const iconMap: Record<string, LucideIcon> = {
  barChart: BarChart,
  users: Users,
  presentation: Presentation,
  graduationCap: GraduationCap,
  code: Code,
  fileCode: FileCode,
  headphones: HeadphonesIcon,
  rocket: Rocket,
  layout: Layout,
  monitorPlay: MonitorPlay,
  bell: Bell,
  briefcase: Briefcase,
  smartphone: Smartphone,
  building: Building,
  school: School,
  globe: Globe,
  award: Award,
  laptop: Laptop,
  building2: Building2,
  helpCircle: HelpCircle,
  youtube: Youtube,
  shield: Shield,
  target: Target,
  bookOpen: BookOpen,
  fileText: FileText,
  lightbulb: Lightbulb,
  brain: Brain,
  megaphone: Megaphone,
  barChart3: BarChart3,
  userCheck: UserCheck,
  dumbbell: Dumbbell
};

export default function WhoIsItForSection({
  badge,
  title,
  description,
  isSecondary,
  id,
  personas
}: WhoIsItForProps): React.JSX.Element {
  return (
    <section
      className="relative container"
      id={id}
    >
      {/* Header */}
      <SiteHeading
        badge={badge}
        title={title}
        isSecondary={isSecondary}
        description={description}
      />

      {/* Cards Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7 sm:gap-8 lg:gap-10">
        {personas?.map((persona, index) => {
          return (
            <BlurFade
              inView
              delay={index * 0.1}
              key={persona.title}
              className="group rounded-2xl bg-card transition-all duration-300 cursor-pointer overflow-hidden border border-border hover:border-primary  shadow-primary/10 hover:-translate-y-1 shadow-sm hover:shadow-lg"
            >
              {/* Illustration Area */}
              <div className="h-40 sm:h-48 bg-gradient-to-br from-slate-50 to-slate-100/50 dark:from-slate-800/50 dark:to-slate-900/30 relative overflow-hidden">
                <div className="relative w-full h-full flex items-center justify-center">
                  <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-28 h-6 bg-gradient-to-t from-slate-200 to-slate-100 dark:from-slate-700 dark:to-slate-600 rounded-full shadow-inner" />
                  <div className="relative bg-white dark:bg-slate-800 rounded-2xl shadow-lg p-4 border border-slate-200/50 dark:border-slate-700/50 animate-[float_3s_ease-in-out_infinite]">
                    <div className="w-14 h-14 bg-primary/10 rounded-xl flex items-center justify-center">
                      {(() => {
                        const Icon = iconMap[persona.icon];
                        return Icon ? (
                          <Icon className="w-7 h-7 text-primary" />
                        ) : null;
                      })()}
                    </div>
                  </div>
                  <div className="absolute top-8 right-10 animate-[float_2.7s_ease-in-out_infinite_0.5s]">
                    <Sparkles className="w-4 h-4 text-primary" />
                  </div>
                </div>
              </div>

              {/* Text Content */}
              <div className="p-5 sm:p-6">
                <h3 className="text-lg sm:text-xl font-bold transition-colors duration-300 mb-2 group-hover:text-primary text-foreground">
                  {persona.title}
                </h3>

                <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
                  {persona.description}
                </p>
              </div>
            </BlurFade>
          );
        })}
      </div>

      {/* Section separator */}
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent"></div>
    </section>
  );
}
